// src/pages/hr/LeavesOverviewPage.jsx
//
// HR -> Leaves Overview
// =====================
//
// PURPOSE
// -------
// This page is the user's leave history / overview screen.
//
// It complements the Dashboard:
//
// - Dashboard = inbox of open / actionable items
// - Leaves Overview = historical / filterable list of the user's leave requests
//
// CURRENT ARCHITECTURE ALIGNMENT
// ------------------------------
// 1) HTTP is authoritative.
//    - We load data only from leave request endpoints.
//    - No local reconstruction of workflow state.
//
// 2) apiClient owns refresh/session recovery.
//    - This page does not try to recover sessions on its own.
//
// 3) Multi-tab-safe auth behavior.
//    - On 401 / session ambiguity / refresh failure we call softLogout()
//      instead of hard logout.
//
// UX
// --
// - filter by date range
// - filter by status
// - open canonical work item details route
//
// IMPORTANT
// ---------
// This page is intentionally about "my leave requests".
// It is not an HR-wide administrative list.

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import { fetchMyLeaveRequestsFiltered } from "../../services/leaveRequestsService";
import { isRequestCanceled } from "../../services/httpUtils";
import { formatLeaveBusinessEnd, formatLeaveBusinessStart } from "../../utils/leaveDateUtils";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function toYMD(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeStatus(status) {
  return String(status || "").trim().toUpperCase();
}

function getErrorMessage(err, fallback) {
  const backendMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    "";

  if (backendMsg) return String(backendMsg);

  const plain = String(err?.message || "").trim();
  return plain || fallback;
}

function isAuthFailure(err) {
  const status = err?.response?.status;
  const code = err?.code;

  return (
    status === 401 ||
    code === "SESSION_ID_REQUIRED" ||
    code === "REFRESH_FAILED"
  );
}

function approverLabel(row) {
  if (row?.approver) {
    const first = row.approver.first_name || "";
    const last = row.approver.last_name || "";
    const full = `${first} ${last}`.trim();
    const username = row.approver.username ? ` (${row.approver.username})` : "";
    return full ? `${full}${username}` : row.approver.username || `User #${row.approver.id}`;
  }

  if (row?.approver_user_id != null) return `User #${row.approver_user_id}`;
  return "—";
}

function leaveTypeLabel(row) {
  return row?.leaveType?.name_pl || row?.leaveType?.name_en || row?.leaveType?.code || "—";
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function LeavesOverviewPage() {
  const { t } = useTranslation(["common", "leaves"]);
  const { authState, authChecked, softLogout } = useContext(AuthContext);
  const navigate = useNavigate();

  const isMountedRef = useRef(false);
  const abortRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  const myUserId = authState?.user?.id ?? null;

  /* ------------------------------------------------------------------------ */
  /* Filters                                                                   */
  /* ------------------------------------------------------------------------ */

  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    return toYMD(d);
  });

  const [to, setTo] = useState(() => toYMD(new Date()));
  const [status, setStatus] = useState("ALL");

  const filters = useMemo(
    () => ({
      from: from || null,
      to: to || null,
      status: status === "ALL" ? null : status,
    }),
    [from, to, status]
  );

  const hasInvalidDateRange = Boolean(from && to && from > to);

  /* ------------------------------------------------------------------------ */
  /* Data state                                                                */
  /* ------------------------------------------------------------------------ */

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const statusOptions = useMemo(
    () => ["ALL", "DRAFT", "PENDING", "APPROVED", "REJECTED", "CANCELLED"],
    []
  );

  /* ------------------------------------------------------------------------ */
  /* Data loading                                                              */
  /* ------------------------------------------------------------------------ */

  const loadRows = useCallback(async () => {
    if (!authChecked || !authState?.isAuthenticated || !myUserId) return;

    if (hasInvalidDateRange) {
      setError(t("leaves:overview.invalidDateRange", { defaultValue: "'From' date cannot be later than 'To' date." }));
      setRows([]);
      setLoading(false);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError("");

    try {
      const list = await fetchMyLeaveRequestsFiltered(filters, {
        signal: controller.signal,
      });

      if (controller.signal.aborted || !isMountedRef.current) return;

      const sorted = [...list].sort((a, b) => {
        const aT = new Date(a.createdAt || a.start_at || 0).getTime();
        const bT = new Date(b.createdAt || b.start_at || 0).getTime();
        return bT - aT;
      });

      setRows(sorted);
    } catch (err) {
      if (isRequestCanceled(err) || controller.signal.aborted) return;

      if (isAuthFailure(err)) {
        softLogout("LEAVES_OVERVIEW_AUTH_FAILURE");
        return;
      }

      if (DEV) {
        // eslint-disable-next-line no-console
        console.error("[LeavesOverviewPage] load failed:", err);
      }

      if (!isMountedRef.current) return;
      setError(getErrorMessage(err, t("leaves:overview.loadError", { defaultValue: "Failed to load leave requests." })));
    } finally {
      if (!controller.signal.aborted && isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [authChecked, authState?.isAuthenticated, myUserId, filters, hasInvalidDateRange, softLogout, t]);

  useEffect(() => {
    if (!authChecked || !authState?.isAuthenticated || !myUserId) return;
    loadRows();

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [authChecked, authState?.isAuthenticated, myUserId, loadRows]);

  /* ------------------------------------------------------------------------ */
  /* Navigation                                                                */
  /* ------------------------------------------------------------------------ */

  const openWorkItem = useCallback(
    (row) => {
      const key = row?.work_item_key || (row?.id ? `leave:${row.id}` : null);
      if (!key) return;

      navigate(`/work-items/${encodeURIComponent(key)}`);
    },
    [navigate]
  );

  /* ------------------------------------------------------------------------ */
  /* Render gating                                                             */
  /* ------------------------------------------------------------------------ */

  if (!authChecked) {
    return (
      <div style={{ padding: 18 }}>
        <h2 style={{ marginTop: 0 }}>{t("leaves:overview.pageTitle")}</h2>
        <div style={{ color: "#6b7280" }}>{t("common:common.loading")}</div>
      </div>
    );
  }

  if (!authState?.isAuthenticated) {
    return (
      <div style={{ padding: 18 }}>
        <h2 style={{ marginTop: 0 }}>{t("leaves:overview.pageTitle")}</h2>
        <div style={{ color: "#6b7280" }}>{t("leaves:overview.notAuthenticated")}</div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div style={{ padding: 18 }}>
      <h2 style={{ marginTop: 0 }}>{t("leaves:overview.pageTitle")}</h2>

      <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
        {t("leaves:overview.pageDescription")}
      </div>

      {/* Filters */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "160px 160px 200px 1fr",
          gap: 10,
          alignItems: "end",
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>{t("leaves:common.from")}</div>
          <input
            className="field-input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>{t("leaves:common.to")}</div>
          <input
            className="field-input"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>{t("leaves:common.status")}</div>
          <select
            className="field-input"
            value={status}
            onChange={(e) => setStatus(normalizeStatus(e.target.value))}
          >
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {t(`leaves:status.${s}`, { defaultValue: s })}
              </option>
            ))}
          </select>
        </div>

        <div
          style={{
            justifySelf: "end",
            display: "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              const d = new Date();
              const fromDate = new Date();
              fromDate.setMonth(fromDate.getMonth() - 2);

              setFrom(toYMD(fromDate));
              setTo(toYMD(d));
              setStatus("ALL");
            }}
          >
            {t("common:actions.reset")}
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={loadRows}
            disabled={loading}
          >
            {t("common:actions.refresh")}
          </button>

          <span style={{ color: "#6b7280", fontSize: 13 }}>
            {loading ? t("common:common.loading") : t("leaves:common.rowCount", { count: rows.length })}
          </span>
        </div>
      </div>

      {error && (
        <div className="msg error" style={{ marginBottom: 10 }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, background: "#fff" }}>
        <div
          style={{
            padding: 12,
            borderBottom: "1px solid #e5e7eb",
            fontWeight: 900,
          }}
        >
          {t("leaves:overview.tableTitle")}
        </div>

        {loading ? (
          <div style={{ padding: 12, color: "#6b7280" }}>{t("common:common.loading")}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 12, color: "#6b7280" }}>
            {t("leaves:overview.noRequests")}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", fontSize: 12, color: "#6b7280" }}>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:common.type")}</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:common.start")}</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:common.end")}</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:common.status")}</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:common.approver")}</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:common.open")}</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: 10, fontWeight: 800 }}>{leaveTypeLabel(row)}</td>
                    <td style={{ padding: 10 }}>{formatLeaveBusinessStart(row)}</td>
                    <td style={{ padding: 10 }}>{formatLeaveBusinessEnd(row)}</td>
                    <td style={{ padding: 10, fontWeight: 800 }}>{t(`leaves:status.${normalizeStatus(row.status)}`, { defaultValue: row.status })}</td>
                    <td style={{ padding: 10 }}>{approverLabel(row)}</td>
                    <td style={{ padding: 10 }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => openWorkItem(row)}
                        type="button"
                      >
                        {t("leaves:common.open")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

